import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { VoteGroupService, ElectionService } from '../services/firebaseService';
import { MagicLinkService } from '../services/magicLinkService';
import { useAuth } from '../contexts/AuthContext';
import { VoteGroup, Election } from '../types';
import CandidatePhoto from '../components/CandidatePhoto';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ArrowRightIcon, 
  ArrowLeftIcon,
  ClockIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const GroupVotePage: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { member, isMember } = useAuth();
  
  const [voteGroup, setVoteGroup] = useState<VoteGroup | null>(null);
  const [elections, setElections] = useState<Election[]>([]);
  const [currentElectionIndex, setCurrentElectionIndex] = useState(0);
  const [votes, setVotes] = useState<{ [electionId: string]: string | null | undefined }>({});
  const [comments, setComments] = useState<{ [electionId: string]: string }>({});
  const [commentErrors, setCommentErrors] = useState<{ [electionId: string]: string }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadVoteGroup = async () => {
      if (!groupId) return;
      
      // Vérifier si l'utilisateur est connecté en tant que membre
      if (!isMember || !member) {
        setError('Vous devez être connecté en tant que membre pour voter');
        setLoading(false);
        return;
      }
      
      try {
        // Vérifier si le membre a déjà voté dans ce groupe
        const hasVoted = await MagicLinkService.hasMemberVotedInGroup(member.email, groupId);
        if (hasVoted) {
          // Rediriger vers la page des choix faits
          navigate(`/vote/choices/${groupId}`);
          return;
        }

        const [groupData, electionsData] = await Promise.all([
          VoteGroupService.getVoteGroupById(groupId),
          ElectionService.getAllElections()
        ]);

        if (!groupData) {
          setError('Groupe d\'élections introuvable');
          return;
        }

        setVoteGroup(groupData);
        
        // Charger les élections du groupe dans l'ordre
        const groupElections = groupData.elections
          .map(electionId => electionsData.find(e => e.id === electionId))
          .filter(Boolean) as Election[];
        
        setElections(groupElections);
        
        // Initialiser les votes (undefined = pas encore voté)
        const initialVotes: { [electionId: string]: string | null | undefined } = {};
        const initialComments: { [electionId: string]: string } = {};
        groupElections.forEach(election => {
          initialVotes[election.id] = undefined;
          initialComments[election.id] = '';
        });
        setVotes(initialVotes);
        setComments(initialComments);

      } catch (error) {
        console.error('Erreur lors du chargement du groupe:', error);
        setError('Erreur lors du chargement des élections');
      } finally {
        setLoading(false);
      }
    };

    loadVoteGroup();
  }, [groupId, member, isMember, navigate]);

  const currentElection = elections[currentElectionIndex];
  const isLastElection = currentElectionIndex === elections.length - 1;
  const isFirstElection = currentElectionIndex === 0;
  const hasVotedInCurrent = votes[currentElection?.id || ''] !== undefined;
  const allElectionsVoted = elections.every(election => votes[election.id] !== undefined);

  // Vérifier si le commentaire obligatoire est vérifié pour l'élection actuelle
  const hasRequiredCommentInCurrent = (() => {
    if (!currentElection) return true;
    const candidateId = votes[currentElection.id];
    if (!candidateId || candidateId === null) return true; // Pas de commentaire requis si abstention
    
    const candidate = currentElection.candidates.find(c => c.id === candidateId);
    if (!candidate || !candidate.allowComments || !candidate.commentRequired) return true;
    
    return comments[currentElection.id]?.trim().length > 0;
  })();

  const handleVote = (candidateId: string | null) => {
    if (!currentElection) return;
    
    setVotes(prev => ({
      ...prev,
      [currentElection.id]: candidateId
    }));
  };

  const handleCommentChange = (electionId: string, comment: string) => {
    setComments(prev => ({
      ...prev,
      [electionId]: comment
    }));
    // Effacer l'erreur du commentaire quand on commence à taper
    if (comment.trim().length > 0) {
      setCommentErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[electionId];
        return newErrors;
      });
    }
  };

  const handleNext = () => {
    setError('');
    if (currentElectionIndex < elections.length - 1) {
      setCurrentElectionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setError('');
    if (currentElectionIndex > 0) {
      setCurrentElectionIndex(prev => prev - 1);
    }
  };

  const handleSubmitAllVotes = async () => {
    const newCommentErrors: { [electionId: string]: string } = {};
    
    if (!allElectionsVoted) {
      setError('Vous devez voter à toutes les élections avant de valider');
      return;
    }

    // Valider tous les commentaires obligatoires et lister les erreurs
    for (const election of elections) {
      const candidateId = votes[election.id];
      
      if (!candidateId || candidateId === null) {
        continue;
      }

      const selectedCandidate = election.candidates.find(c => c.id === candidateId);
      
      if (!selectedCandidate) continue;

      // Vérifier si un commentaire obligatoire est requis
      if (selectedCandidate.allowComments && selectedCandidate.commentRequired) {
        if (!comments[election.id] || comments[election.id].trim() === '') {
          newCommentErrors[election.id] = `Commentaire obligatoire pour "${selectedCandidate.name}"`;
        }
      }
    }

    // S'il y a des erreurs, afficher les erreurs et naviguer vers la première
    if (Object.keys(newCommentErrors).length > 0) {
      setCommentErrors(newCommentErrors);
      setError(''); // Pas de message d'erreur global
      
      // Trouver la première élection avec erreur et y naviguer silencieusement
      const firstErrorElectionId = Object.keys(newCommentErrors)[0];
      const electionIndex = elections.findIndex(e => e.id === firstErrorElectionId);
      if (electionIndex !== -1) {
        setCurrentElectionIndex(electionIndex);
      }
      return;
    }

    // Effacer les erreurs et rediriger
    setCommentErrors({});
    setError('');
    navigate(`/vote/summary/${groupId}`, { 
      state: { votes, comments } 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mx-auto mb-4"></div>
          <p className="text-white text-xl">Chargement des élections...</p>
        </div>
      </div>
    );
  }

  if (error || !voteGroup || !currentElection) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-red-800 to-red-900 flex items-center justify-center">
        <div className="text-center bg-white rounded-lg p-8 max-w-md mx-4">
          <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-4">{error || 'Élection introuvable'}</p>
          <button 
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
      {/* En-tête */}
      <div className="bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                {voteGroup.name}
              </h1>
              <p className="text-blue-100">{voteGroup.description}</p>
            </div>
            <div className="text-right">
              <div className="flex items-center text-white mb-2">
                <ShieldCheckIcon className="w-5 h-5 mr-2" />
                <span className="text-sm">Vote Sécurisé</span>
              </div>
              <div className="flex items-center text-blue-100">
                <ClockIcon className="w-4 h-4 mr-1" />
                <span className="text-sm">
                  {new Date().toLocaleDateString('fr-FR')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progression */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white font-medium">
              Élection {currentElectionIndex + 1} sur {elections.length}
            </span>
            <span className="text-blue-100">
              {Math.round(((currentElectionIndex + 1) / elections.length) * 100)}%
            </span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2">
            <div 
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentElectionIndex + 1) / elections.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Élection actuelle */}
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
          {/* En-tête de l'élection */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
            <h2 className="text-2xl font-bold text-white mb-2">
              {currentElection.title}
            </h2>
            <p className="text-blue-100">{currentElection.position}</p>
            {currentElection.description && (
              <p className="text-blue-200 mt-2">{currentElection.description}</p>
            )}
          </div>

          {/* Candidats */}
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentElection.candidates.map((candidate) => {
                const isSelected = votes[currentElection.id] === candidate.id;
                
                return (
                  <div
                    key={candidate.id}
                    onClick={() => handleVote(candidate.id)}
                    className={`relative cursor-pointer transition-all duration-200 transform hover:scale-105 ${
                      isSelected 
                        ? 'ring-4 ring-green-500 bg-green-50' 
                        : 'hover:bg-gray-50'
                    } border-2 rounded-xl p-6 ${
                      isSelected 
                        ? 'border-green-500' 
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    {/* Photo du candidat */}
                    <div className="flex justify-center mb-4">
                      <CandidatePhoto 
                        photo={candidate.photo} 
                        name={candidate.name} 
                        size="xl" 
                        className="w-24 h-24"
                      />
                    </div>

                    {/* Nom du candidat */}
                    <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                      {candidate.name}
                    </h3>

                    {/* Position */}
                    <p className="text-sm text-gray-600 text-center mb-3">
                      {candidate.position}
                    </p>

                    {/* Biographie */}
                    {candidate.bio && (
                      <p className="text-xs text-gray-500 text-center mb-4 line-clamp-3">
                        {candidate.bio}
                      </p>
                    )}

                    {/* Indicateur de sélection */}
                    {isSelected && (
                      <div className="absolute top-4 right-4">
                        <CheckCircleIcon className="w-6 h-6 text-green-500" />
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Option d'abstention */}
              {currentElection.allowAbstention && (
                <div
                  onClick={() => handleVote(null)}
                  className={`relative cursor-pointer transition-all duration-200 transform hover:scale-105 ${
                    votes[currentElection.id] === null 
                      ? 'ring-4 ring-orange-500 bg-orange-50' 
                      : 'hover:bg-gray-50'
                  } border-2 rounded-xl p-6 ${
                    votes[currentElection.id] === null 
                      ? 'border-orange-500' 
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                >
                  <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                    <XCircleIcon className="w-12 h-12 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                    S'abstenir
                  </h3>
                  <p className="text-sm text-gray-600 text-center">
                    Je ne souhaite pas voter pour cette élection
                  </p>
                  {votes[currentElection.id] === null && (
                    <div className="absolute top-4 right-4">
                      <CheckCircleIcon className="w-6 h-6 text-orange-500" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Section Commentaires (si requis) */}
          {votes[currentElection.id] && votes[currentElection.id] !== null && (
            (() => {
              const selectedCandidate = currentElection.candidates.find(c => c.id === votes[currentElection.id]);
              return selectedCandidate && selectedCandidate.allowComments ? (
                <div className="bg-gray-50 px-8 py-6 border-t">
                  <div className="max-w-2xl">
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      {selectedCandidate.commentLabel || 'Commentaire'}
                      {selectedCandidate.commentRequired && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </label>
                    <textarea
                      value={comments[currentElection.id] || ''}
                      onChange={(e) => handleCommentChange(currentElection.id, e.target.value)}
                      className={`input-field ${commentErrors[currentElection.id] ? 'border-red-500 bg-red-50' : ''}`}
                      rows={3}
                      placeholder={selectedCandidate.commentPlaceholder || 'Ajouter un commentaire...'}
                    />
                    {commentErrors[currentElection.id] && (
                      <p className="text-sm text-red-600 mt-2 flex items-center">
                        <span className="inline-block w-1.5 h-1.5 bg-red-600 rounded-full mr-2"></span>
                        {commentErrors[currentElection.id]}
                      </p>
                    )}
                    {!commentErrors[currentElection.id] && (
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedCandidate.commentRequired ? 'Commentaire obligatoire' : 'Commentaire optionnel'}
                      </p>
                    )}
                  </div>
                </div>
              ) : null;
            })()
          )}

          {/* Navigation */}
          <div className="bg-gray-50 px-8 py-6 flex items-center justify-between">
            <button
              onClick={handlePrevious}
              disabled={isFirstElection}
              className={`flex items-center px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                isFirstElection
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
              }`}
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Précédent
            </button>

            <div className="flex items-center space-x-4">
              {hasVotedInCurrent && (
                <div className="flex items-center text-green-600">
                  <CheckCircleIcon className="w-5 h-5 mr-2" />
                  <span className="text-sm font-medium">Vote enregistré</span>
                </div>
              )}

              {isLastElection ? (
                <button
                  onClick={handleSubmitAllVotes}
                  disabled={!allElectionsVoted}
                  className={`flex items-center px-8 py-3 rounded-lg font-medium transition-all duration-200 ${
                    allElectionsVoted
                      ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <CheckCircleIcon className="w-5 h-5 mr-2" />
                  Valider tous les votes
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={!hasVotedInCurrent || !hasRequiredCommentInCurrent}
                  className={`flex items-center px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    hasVotedInCurrent && hasRequiredCommentInCurrent
                      ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  title={!hasRequiredCommentInCurrent ? 'Veuillez ajouter le commentaire obligatoire' : ''}
                >
                  Suivant
                  <ArrowRightIcon className="w-5 h-5 ml-2" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupVotePage;
